import * as React from 'react';
import * as navigate from 'app/area/shared/navigation';
import { Paper, PromptConfirmation } from 'ui';
import { withPrompt, PromptProps, SpinLoader } from 'app/components';
import { STATE } from 'app/constants';
import { isDefinedIncludingZero } from 'app/infrastructure/data/number';
import { containerFactory, DefaultContainerProps } from 'app/infrastructure/redux';
import { OptionsProps, withOptions } from 'app/components/options-handler';
import { experienceTransferTranslation } from '../constants';
import { ExperienceTransferState } from '../types-store';
import { actionCreators } from './actions';
import { ExperienceTransferFormSelectorProps, selectors } from './selectors';
import { ExperienceTransferItem } from './types-store';
import { scrollToTop } from 'app/infrastructure/platform/routing';
import { ExperienceTransferActionType } from 'app/area/experience-transfer/form/types';
import { getExperienceTransferAdminAccess } from 'app/area/experience-transfer/get-access';
import { ExperienceTransferFormRenderer } from 'app/area/experience-transfer/form/components/experience-transfer-form-renderer';
import { routeConstants } from 'app/routes/route-constants';

type ExperienceTransferFormProps = DefaultContainerProps
  & OptionsProps
  & PromptProps
  & ExperienceTransferState
  & ExperienceTransferFormSelectorProps
  & typeof actionCreators;

class ExperienceTransferFormComponent extends React.PureComponent<ExperienceTransferFormProps> {
  public static statePath: string = STATE.experienceTransfer;

  public componentDidMount() {
    scrollToTop();
    this.initForm();
  }

  public componentDidUpdate(prevProps: ExperienceTransferFormProps) {
    if (this.props.id !== prevProps.id) {
      this.initForm();
    }
  }

  public componentWillUnmount() {
    this.props.clearForm();
  }

  private initForm = () => {
    const { id, sharedState, createExperienceTransfer, loadExperienceTransfer, displayError, translation } = this.props;

    const experienceTransferAccess = getExperienceTransferAdminAccess(sharedState);
    if (!experienceTransferAccess) {
      displayError(translation.no_access_to_experience_transfer);
      navigate.toStartupView(true);
      return;
    }

    if (isDefinedIncludingZero(id)) {
      loadExperienceTransfer(id);
    } else {
      createExperienceTransfer();
    }
  };

  private saveExperienceTransfer = (experienceTransferItem: ExperienceTransferItem) => {
    const {
      params: { actionType } = {}, saveExperienceTransfer, translation } = this.props;

    const actionTypeId = ExperienceTransferActionType[actionType];
    const successMessage = (isDefinedIncludingZero(experienceTransferItem?.id)
      && actionTypeId === ExperienceTransferActionType.edit)
      ? translation.experience_transfer_updated
      : translation.experience_transfer_created;

    saveExperienceTransfer(actionTypeId, experienceTransferItem, successMessage,
      translation.experience_transfer_validation_error);
  };

  private deleteExperienceTransfer = () => {
    const { id, form: { experienceTransferItem: { subscriberGroup } = {} } = {},
      translation, deleteExperienceTransfer } = this.props;

    deleteExperienceTransfer(
      id,
      subscriberGroup?.description,
      translation.experience_transfer_deleted
    );
  };

  private onContinue = () => {
    navigate.to({ pathname: routeConstants.experienceTransferList });
  };

  public render() {
    const { sharedState, form, translation,
      options, shouldPrompt,
      params: { actionType },
      updateItem, resetValidation, updateValidation, clearValidation, displayError, loadOptions
    } = this.props;

    const locale = sharedState?.locale || '';


    return <div className="entity-view">
      <Paper componentId="experience-transfer-paper" className={undefined}>
        {!!this.props.shouldDisplayModal &&
          <PromptConfirmation
            title={translation.cancel_changes}
            description={translation.are_you_sure}
            continueText={translation.undo_all}
            cancelText={translation.go_back}
            onContinue={this.onContinue}
            onCancel={this.props.cancel} />}

        {!form.experienceTransferItem || form.fetching
          ? <SpinLoader />
          : <ExperienceTransferFormRenderer
            actionType={actionType}
            formState={form}
            shouldPrompt={shouldPrompt}
            saveExperienceTransfer={this.saveExperienceTransfer}
            deleteExperienceTransfer={this.deleteExperienceTransfer}
            updateItem={updateItem}
            options={options}
            loadOptions={loadOptions}
            updateValidation={updateValidation}
            displayError={displayError}
            locale={locale}
            translation={translation}
            resetValidation={resetValidation}
            clearValidation={clearValidation} />}
      </Paper>
    </div>;
  }
}

const container = containerFactory(
  withPrompt(withOptions(ExperienceTransferFormComponent)),
  actionCreators, selectors, experienceTransferTranslation
);

export { container as ExperienceTransferForm };
